import React, { useEffect, useMemo, useState } from 'react';
import {
  DataGrid,
  GridColDef,
  CellTypes,
  DataGridProps,
  DataGridUtils,
} from '@fashionnovateam/nova-ui-toolkit';
import moment from 'moment/moment';
import { User, Vendor } from '../../Lib/Api/vendor';
import Link from '../ReusableComponents/Link/Link';
import Dot from '../ReusableComponents/Dot/Dot';
import poItemsMissingDims from '../FnCatalogV2/utils/poItemsMissingDims';
import { PurchaseOrder } from '../../Lib/Types/purchase-order';
import { hasAccessTo, RolesType } from '../../Lib/Constants/roles';

type Row = {
  id: string;
  reference: CellTypes.DataGridCellBadgeParams;
  starts_at: CellTypes.DataGridCellDateTimeParams;
  time: CellTypes.DataGridCellStringParams;
  delivered_date: CellTypes.DataGridCellDateTimeParams;
  boxes: CellTypes.DataGridCellStringParams;
  units: CellTypes.DataGridCellStringParams;
  purchase_orders: PurchaseOrder[];
};

interface Delivery {
  booking_id: string;
  boxes: number;
  boxes_received: string;
  cancellation_reason: string | null;
  created_by: number;
  delivered_at: string;
  ends_at: string;
  id: string;
  location_id: string;
  notes: string | null;
  purchase_orders: PurchaseOrder[];
  reference: string;
  starts_at: string;
  type: 'ORDER_DELIVERY';
  units: number;
  vendor: Vendor;
}

const columns: GridColDef[] = [
  {
    field: 'reference',
    headerName: 'Reference',
    render: 'badge',
    sortable: true,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'starts_at',
    headerName: 'Date',
    render: 'dateTime',
    sortable: true,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'time',
    headerName: 'Time',
    render: 'string',
    display: 'flex',
    sortable: false,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'delivered_date',
    headerName: 'Delivered Date',
    render: 'dateTime',
    display: 'flex',
    sortable: false,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'boxes',
    headerName: 'Box',
    render: 'string',
    display: 'flex',
    sortable: false,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'units',
    headerName: 'Units',
    render: 'string',
    display: 'flex',
    sortable: false,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'pos',
    headerName: 'POs',
    render: 'custom',
    display: 'flex',
    sortable: false,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'dim',
    headerName: 'Dimensions',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: false,
    minWidth: 200,
    disableColumnMenu: true,
  },
];

type Props = {
  rows: Delivery[];
  currentUser: User;
  targetUserId: string;
} & Omit<DataGridProps<Row>, 'columns'>;

function VendorDeliveriesDataGrid({
  rows,
  currentUser,
  targetUserId,
  ...datagridProps
}: Props) {
  const apiRef = DataGridUtils.useGridApiRef();
  const [columnWidths, setColumnWidths] = useState({ dim: 200, po: 150 });

  const transformedRows = useMemo(
    () =>
      Object.values(rows).map((delivery: Delivery) => ({
        id: delivery.id,
        reference: {
          children: delivery.reference,
          contrast: 'low',
        },
        starts_at: {
          activity: false,
          time: false,
          date: delivery.starts_at,
          timeZone: 'UTC',
        },
        time: moment.utc(delivery.starts_at).format('hh:mm A'),
        delivered_date: {
          activity: false,
          time: false,
          date: delivery.delivered_at,
        },
        boxes: delivery.boxes.toString(),
        units: delivery.units.toString(),
        purchase_orders: delivery.purchase_orders,
      })),
    [rows],
  );

  useEffect(() => {
    if (apiRef?.current?.setExpandedDetailPanels) {
      apiRef?.current?.setExpandedDetailPanels(
        transformedRows.map((d) => d.id),
      );
    }
  }, [transformedRows]);

  const updateColumnWidths = () => {
    if (apiRef?.current?.getColumn) {
      const posColumn = apiRef.current.getColumn('pos');
      const dimColumn = apiRef.current.getColumn('dim');
      setColumnWidths({
        po: posColumn ? posColumn.computedWidth : 100,
        dim: dimColumn ? dimColumn.computedWidth : 200,
      });
    }
  };

  const hasAccessToPOs = useMemo(
    () =>
      hasAccessTo('users/:id/purchase-orders', currentUser.role as RolesType),
    [currentUser],
  );

  useEffect(() => {
    updateColumnWidths();
  }, [apiRef.current, transformedRows]);

  const getDetailPanelContent = ({ row }: { row: Row }) => (
    <div>
      {row?.purchase_orders?.map((po) => {
        const dimsMissing = poItemsMissingDims(po);
        return (
          <div
            key={po.po_number}
            className="flex h-[50px] items-center justify-end border-t border-neutral-100 border-0"
          >
            {hasAccessToPOs ? (
              <Link
                style={{ width: columnWidths.po }}
                to={`/users/${targetUserId}/purchase-orders/${po.po_number}`}
              >
                {po.po_number}
              </Link>
            ) : (
              <div>{po.po_number}</div>
            )}
            <span
              style={{
                width: columnWidths.dim,
              }}
            >
              <Dot color={dimsMissing === 0 ? 'green' : 'red'} />
              {dimsMissing !== 0 && hasAccessToPOs && (
                <Link
                  to={`/users/${targetUserId}/purchase-orders/${po.po_number}`}
                >
                  Add
                </Link>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <DataGrid
      apiRef={apiRef}
      hideFilterButton
      hideGroupButton
      columns={columns}
      onColumnResize={updateColumnWidths}
      rows={transformedRows}
      sortingMode="server"
      paginationMode="server"
      {...datagridProps}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={() => 'auto'}
    />
  );
}

export default VendorDeliveriesDataGrid;
